// src/avatar.anatomy.ts
import { createAnatomy } from "@zag-js/anatomy";
var anatomy = createAnatomy("avatar").parts("root", "image", "fallback");
var parts = anatomy.build();

// src/avatar.dom.ts
import { createScope } from "@zag-js/dom-query";
var dom = createScope({
  getRootId: (ctx) => `avatar:${ctx.id}`,
  getImageId: (ctx) => `avatar:${ctx.id}:image`,
  getFallbackId: (ctx) => `avatar:${ctx.id}:fallback`,
  getRootEl: (ctx) => dom.getById(ctx, dom.getRootId(ctx)),
  getImageEl: (ctx) => dom.getById(ctx, dom.getImageId(ctx))
});

// src/avatar.connect.ts
function connect(state, send, normalize) {
  const isLoaded = state.matches("loaded");
  const showFallback = !isLoaded;
  return {
    isLoaded,
    showFallback,
    setSrc(src) {
      send({ type: "SRC.SET", src });
    },
    setLoaded() {
      send({ type: "IMG.LOADED", src: "api" });
    },
    setError() {
      send({ type: "IMG.ERROR", src: "api" });
    },
    rootProps: normalize.element({
      ...parts.root.attrs,
      dir: state.context.dir,
      id: dom.getRootId(state.context),
      style: {
        display: "grid",
        gridTemplateRows: "1fr 1fr",
        overflow: "hidden"
      }
    }),
    imageProps: normalize.img({
      ...parts.image.attrs,
      dir: state.context.dir,
      id: dom.getImageId(state.context),
      "data-state": isLoaded ? "visible" : "hidden",
      onLoad() {
        send({ type: "IMG.LOADED", src: "element" });
      },
      onError() {
        send({ type: "IMG.ERROR", src: "element" });
      },
      style: {
        gridArea: "1 / 1 / 2 / 2",
        visibility: !isLoaded ? "hidden" : void 0
      }
    }),
    fallbackProps: normalize.element({
      ...parts.fallback.attrs,
      dir: state.context.dir,
      id: dom.getFallbackId(state.context),
      hidden: !showFallback,
      "data-state": isLoaded ? "hidden" : "visible",
      style: {
        gridArea: "1 / 1 / 2 / 2"
      }
    })
  };
}

// src/avatar.machine.ts
import { createMachine } from "@zag-js/core";
import { observeAttributes, observeChildren } from "@zag-js/mutation-observer";
import { compact } from "@zag-js/utils";
function machine(userContext) {
  const ctx = compact(userContext);
  return createMachine(
    {
      id: "avatar",
      initial: "loading",
      activities: ["trackImageRemoval"],
      context: ctx,
      on: {
        "SRC.CHANGE": {
          target: "loading"
        },
        "IMG.UNMOUNT": {
          target: "error"
        }
      },
      states: {
        loading: {
          activities: ["trackSrcChange"],
          entry: ["checkImgStatus"],
          on: {
            "IMG.LOADED": {
              target: "loaded",
              actions: ["invokeOnLoad"]
            },
            "IMG.ERROR": {
              target: "error",
              actions: ["invokeOnError"]
            }
          }
        },
        error: {
          activities: ["trackSrcChange"],
          on: {
            "IMG.LOADED": {
              target: "loaded",
              actions: ["invokeOnLoad"]
            }
          }
        },
        loaded: {
          activities: ["trackSrcChange"],
          on: {
            "IMG.ERROR": {
              target: "error",
              actions: ["invokeOnError"]
            }
          }
        }
      }
    },
    {
      activities: {
        trackSrcChange(ctx2, _evt, { send }) {
          const img = dom.getImageEl(ctx2);
          return observeAttributes(img, ["src", "srcset"], () => {
            send({ type: "SRC.CHANGE" });
          });
        },
        trackImageRemoval(ctx2, _evt, { send }) {
          const rootEl = dom.getRootEl(ctx2);
          return observeChildren(rootEl, (records) => {
            const removedNodes = Array.from(records[0].removedNodes);
            const removed = removedNodes.find((node) => node.matches("[data-scope=avatar][data-part=image]"));
            if (removed) {
              send({ type: "IMG.UNMOUNT" });
            }
          });
        }
      },
      actions: {
        invokeOnLoad(ctx2) {
          ctx2.onLoadingStatusChange?.({ status: "loaded" });
        },
        invokeOnError(ctx2) {
          ctx2.onLoadingStatusChange?.({ status: "error" });
        },
        checkImgStatus(ctx2, _evt, { send }) {
          const img = dom.getImageEl(ctx2);
          if (img?.complete) {
            const type = img.currentSrc ? "IMG.LOADED" : "IMG.ERROR";
            send({ type, src: "ssr" });
          }
        }
      }
    }
  );
}

// src/avatar.props.ts
import { createProps } from "@zag-js/types";
import { createSplitProps } from "@zag-js/utils";
var props = createProps()(["dir", "id", "onLoadingStatusChange", "getRootNode"]);
var splitProps = createSplitProps(props);
export {
  anatomy,
  connect,
  machine,
  props,
  splitProps
};
//# sourceMappingURL=index.mjs.map